import { useState } from "react";
import { Avatar, Box, Button, Link, Typography } from "@mui/material";
import moment from "moment";
import inkfreeLogo from "assets/images/side_nav_inkfree_logo.svg";
import urls from "global/constants/UrlConstants";
import { RequestFile, SignRequest } from "models/interfaces";
import { getContactTint } from "screens/Contacts/Contacts.tokens";
import SignerCard from "./SignerCard";

interface Props {
  signRequest: SignRequest;
  reqToken: string;
  /** Open the document viewer so the reviewer can read before deciding. */
  onReview: () => void;
  /** Approve the request (backend approve). */
  onApprove: () => void;
  /** Reject the request (backend reject / send back). */
  onReject: () => void;
  loading?: boolean;
}

const INITIAL_COUNT = 3;

/** Sender initials use the first Contacts avatar tint — one sender, so no cycling. */
const senderTint = getContactTint(0);

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

const getFileName = (file: RequestFile, index: number) => {
  const raw = (file?.unsignedPdf || file?.unsignedDoc || "") as string;
  const base = raw.includes("/") ? raw.substring(raw.lastIndexOf("/") + 1) : raw;
  return base || `Document ${index + 1}`;
};

/**
 * "Approval Invite Landing" card for reviewers — the approver counterpart of
 * SigningInviteCard, sharing the same SignerCard chrome, signer.* palette and
 * document-list treatment so the review flow matches the sign flow. Sender
 * header, a short "what approving/rejecting means" note, the document list
 * (name + meta + Review), an expiry callout, and Reject / Approve actions.
 */
const ApprovalInviteCard = ({
  signRequest,
  reqToken,
  onReview,
  onApprove,
  onReject,
  loading = false,
}: Props) => {
  const [showAll, setShowAll] = useState(false);

  const senderName = signRequest?.requestedBy?.name || "";
  const senderEmail = signRequest?.requestedBy?.email || "";
  const message = signRequest?.emailMessage || "";
  const files = signRequest?.files || [];
  const docCount = files.length;
  const totalPages = files.reduce((n, f) => n + (f?.pageCount || 0), 0);
  const expirationDays = signRequest?.expirationDays;

  // Expiry date = request creation date + expirationDays (falls back to today
  // if the created date isn't in the payload).
  const hasExpiry = Number.isFinite(expirationDays) && expirationDays > 0;
  const expiryMoment = hasExpiry
    ? moment(signRequest?.createdOn || undefined).add(expirationDays, "days")
    : null;
  const daysLeft = expiryMoment ? expiryMoment.diff(moment(), "days") : null;

  const fileMeta = (file: RequestFile) => {
    const pages = file?.pageCount || 0;
    const parts: string[] = [];
    if (pages > 0) parts.push(`${pages} page${pages > 1 ? "s" : ""}`);
    parts.push("sent for your approval");
    return parts.join(" · ");
  };

  const visibleFiles = showAll ? files : files.slice(0, INITIAL_COUNT);

  return (
    <SignerCard
      maxWidth={600}
      sx={{ maxHeight: "calc(100vh - 40px)", overflowY: "auto" }}
    >
      {/* header: logo + secure badge */}
      <Box
        sx={{
          px: { xs: 3, sm: 5.5 },
          pt: 4.25,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box component="img" src={inkfreeLogo} alt="Inkfree" sx={{ width: 100 }} />
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.9,
            height: 28,
            px: 1.5,
            borderRadius: "999px",
            backgroundColor: "signer.success.tint",
            border: "1px solid",
            borderColor: "signer.success.border",
            color: "signer.success.main",
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={13} height={13}>
            <path d="M12 3l7 3v5c0 4.5-3 8.2-7 9.5-4-1.3-7-5-7-9.5V6l7-3z" />
            <path d="M9.5 12l2 2 3.5-4" />
          </svg>
          <Typography sx={{ fontSize: "0.6875rem", fontWeight: 700, letterSpacing: "0.03em" }}>
            Secure request
          </Typography>
        </Box>
      </Box>

      {/* sender */}
      <Box sx={{ px: { xs: 3, sm: 5.5 }, pt: 3.25, pb: 3.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
          <Avatar
            sx={{
              width: 46,
              height: 46,
              flexShrink: 0,
              bgcolor: senderTint.bg,
              color: senderTint.color,
              fontSize: "0.9375rem",
              fontWeight: 700,
              textTransform: "uppercase",
            }}
          >
            {getInitials(senderName) || "IK"}
          </Avatar>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, minWidth: 0 }}>
            {/* The page's only heading, so screen-reader users can jump to it (1.3.1). */}
            <Typography component="h1" sx={{ fontSize: "1rem", fontWeight: 700, color: "signer.ink" }}>
              {senderName || "Someone"} requested your approval
            </Typography>
            <Typography sx={{ fontSize: "0.78rem", color: "signer.text.meta" }}>
              {senderEmail ? `${senderEmail} · ` : ""}via Inkfree e-Sign
            </Typography>
          </Box>
        </Box>

        {/* what approving / rejecting means */}
        <Typography
          sx={{
            mt: 2.25,
            fontSize: "0.83rem",
            lineHeight: 1.6,
            color: "signer.text.secondary",
          }}
        >
          Approving confirms you’re satisfied with the document’s contents.
          Rejecting sends it back to {senderName || "the sender"} with your
          feedback.
        </Typography>

        {/* optional personal message */}
        {message && (
          <Box
            sx={{
              mt: 2.25,
              px: 2.25,
              py: 1.75,
              borderLeft: "3px solid",
              borderColor: "signer.border.main",
              backgroundColor: "signer.surface.subtle",
              borderRadius: "0 10px 10px 0",
            }}
          >
            <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.6, fontStyle: "italic", color: "signer.text.secondary" }}>
              “{message}”
            </Typography>
          </Box>
        )}

        {/* document list */}
        <Box
          sx={{
            mt: 2.5,
            border: "1px solid",
            borderColor: "signer.border.light",
            borderRadius: "14px",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              px: 2.25,
              py: 1.5,
              backgroundColor: "signer.surface.subtle",
              borderBottom: "1px solid",
              borderColor: "signer.border.light",
            }}
          >
            <Typography sx={{ fontSize: "0.75rem", fontWeight: 800, color: "signer.ink", letterSpacing: "0.02em" }}>
              {docCount} {docCount === 1 ? "document" : "documents"} to review
            </Typography>
            {totalPages > 0 && (
              <Typography sx={{ fontSize: "0.72rem", fontWeight: 600, color: "signer.text.meta" }}>
                {totalPages} {totalPages === 1 ? "page" : "pages"} total
              </Typography>
            )}
          </Box>

          <Box sx={{ maxHeight: 288, overflowY: "auto" }}>
            {visibleFiles.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.75,
                  px: 2.25,
                  py: 1.75,
                  backgroundColor: "background.paper",
                  borderTop: idx === 0 ? "none" : "1px solid",
                  borderColor: "signer.border.light",
                }}
              >
                <Box
                  sx={{
                    width: 38,
                    height: 48,
                    flexShrink: 0,
                    borderRadius: "6px",
                    border: "1px solid",
                    borderColor: "signer.border.light",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: (t) =>
                      `repeating-linear-gradient(0deg, ${t.palette.background.paper} 0 6px, ${t.palette.signer.surface.muted} 6px 8px)`,
                  }}
                >
                  <Typography sx={{ fontSize: "0.5rem", fontWeight: 800, letterSpacing: "0.06em", color: "signer.orange.main" }}>
                    PDF
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 0.4 }}>
                  <Typography
                    sx={{
                      fontSize: "0.84rem",
                      fontWeight: 700,
                      color: "signer.ink",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {getFileName(file, idx)}
                  </Typography>
                  <Typography sx={{ fontSize: "0.72rem", color: "signer.text.meta" }}>
                    {fileMeta(file)}
                  </Typography>
                </Box>
                <Button
                  onClick={onReview}
                  variant="outlined"
                  sx={{
                    flexShrink: 0,
                    height: 32,
                    px: 1.5,
                    borderRadius: "8px",
                    textTransform: "none",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    color: "signer.orange.main",
                    borderColor: "signer.border.main",
                    borderWidth: "1.5px",
                    "&:hover": {
                      borderWidth: "1.5px",
                      borderColor: "signer.orange.main",
                      backgroundColor: "signer.orange.faintest",
                    },
                  }}
                >
                  Review
                </Button>
              </Box>
            ))}
          </Box>

          {docCount > INITIAL_COUNT && (
            <Button
              onClick={() => setShowAll((v) => !v)}
              fullWidth
              endIcon={
                <svg
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  width={11}
                  height={11}
                  style={{ transform: showAll ? "rotate(180deg)" : "none" }}
                >
                  <path d="M6 9l6 6 6-6" />
                </svg>
              }
              sx={{
                height: 38,
                borderTop: "1px solid",
                borderColor: "signer.border.light",
                borderRadius: 0,
                textTransform: "none",
                fontSize: "0.78rem",
                fontWeight: 700,
                color: "signer.orange.main",
                "&:hover": { backgroundColor: "signer.orange.faintest" },
              }}
            >
              {showAll ? "Show fewer" : `Show all ${docCount} documents`}
            </Button>
          )}

          {expiryMoment && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 2.25,
                py: 1.25,
                backgroundColor: "signer.warn.tint",
                borderTop: "1px solid",
                borderColor: "signer.warn.border",
                color: "signer.warn.main",
              }}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} width={13} height={13}>
                <circle cx="12" cy="12" r="9" />
                <path d="M12 7v5l3.2 2" />
              </svg>
              <Typography sx={{ fontSize: "0.75rem", fontWeight: 600, color: "signer.warn.text" }}>
                Link expires on {expiryMoment.format("ddd, MMM D, YYYY")}
                {daysLeft != null && daysLeft >= 0
                  ? ` · ${daysLeft} ${daysLeft === 1 ? "day" : "days"} left`
                  : ""}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* actions */}
      <Box
        sx={{
          px: { xs: 3, sm: 5.5 },
          pb: 3.5,
          display: "flex",
          gap: 1.75,
        }}
      >
        <Button
          onClick={onReject}
          disabled={loading}
          variant="outlined"
          startIcon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2} strokeLinecap="round" width={16} height={16}>
              <path d="M18 6L6 18" />
              <path d="M6 6l12 12" />
            </svg>
          }
          sx={{
            flex: 1,
            height: 50,
            borderRadius: "11px",
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 700,
            color: "signer.danger.main",
            borderColor: "signer.border.main",
            borderWidth: "1.5px",
            "&:hover": {
              borderWidth: "1.5px",
              borderColor: "signer.danger.main",
              backgroundColor: "signer.surface.subtle",
            },
          }}
        >
          Reject
        </Button>
        <Button
          onClick={onApprove}
          disabled={loading}
          startIcon={
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round" strokeLinejoin="round" width={17} height={17}>
              <path d="M5 12l5 5L20 6" />
            </svg>
          }
          sx={{
            flex: 1,
            height: 50,
            borderRadius: "11px",
            textTransform: "none",
            fontSize: "0.95rem",
            fontWeight: 700,
            color: "common.white",
            backgroundColor: "primary.main",
            boxShadow: "none",
            "&:hover": { backgroundColor: "primary.dark", boxShadow: "none" },
            "&.Mui-disabled": { color: "common.white", opacity: 0.6 },
          }}
        >
          Approve
        </Button>
      </Box>

      {/* legal footer */}
      <Box
        sx={{
          px: { xs: 3, sm: 5.5 },
          py: 1.75,
          borderTop: "1px solid",
          borderColor: "signer.border.light",
          backgroundColor: "signer.surface.subtle",
          textAlign: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: "0.6875rem",
            lineHeight: 1.6,
            color: "signer.text.muted",
          }}
        >
          By continuing you agree that e-signatures are legally binding.{" "}
          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
            <Link
              href={urls.tNcUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontWeight: 600, color: "signer.orange.dark" }}
            >
              Terms
            </Link>{" "}
            ·{" "}
            <Link
              href={urls.privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              underline="hover"
              sx={{ fontWeight: 600, color: "signer.orange.dark" }}
            >
              Privacy
            </Link>
          </Box>
        </Typography>
      </Box>
    </SignerCard>
  );
};

export default ApprovalInviteCard;
